import { LEGAL_LINKS, TURNSTILE_ACTIONS } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { Button } from "@sagot/ui";
import { useState, type SyntheticEvent } from "react";
import { FormAlert } from "./components/form-alert";
import { Turnstile } from "./components/turnstile";
import { CheckGlyph } from "./register-icons";
import { PaneTitle, RegisterNav, RegisterPane } from "./register-pane";

interface RegisterConsentsProps {
  terms: boolean;
  dpa: boolean;
  marketing: boolean;
  onChange: (patch: { terms?: boolean; dpa?: boolean; marketing?: boolean }) => void;
  /** Hands over the security-check token with the sign-up. */
  onSubmit: (captchaToken: string) => void;
  submitting: boolean;
  error: string | null;
  onDismissError: () => void;
  /** Changes after a refused sign-up so the check is issued again (a token works once). */
  captchaReset: number;
  back: boolean;
  onBack: () => void;
}

/** Security-check row (mockup `.captcha`): driven by the Cloudflare Turnstile token, which arrives by itself unless a challenge is shown. */
function SecurityCheck({ passed }: { passed: boolean }) {
  const { t } = useTranslation();
  return (
    <div className="captcha" data-state={passed ? "done" : "busy"}>
      <span className="cb" aria-hidden="true">
        <CheckGlyph />
      </span>
      <span role="status">{passed ? t("auth.register.captchaPassed") : t("auth.register.captchaChecking")}</span>
      <span className="mono">{t("auth.register.captchaTag")}</span>
    </div>
  );
}

/** Step 4 (mockup pane 5): the two required agreements, the optional one and the security check; Create workspace sends the sign-up. */
export function RegisterConsents({ terms, dpa, marketing, onChange, onSubmit, submitting, error, onDismissError, captchaReset, back, onBack }: RegisterConsentsProps) {
  const { t } = useTranslation();
  const [token, setToken] = useState<string | null>(null);
  const ready = terms && dpa && token !== null;

  const submit = (event: SyntheticEvent) => {
    event.preventDefault();
    if (ready && !submitting) onSubmit(token);
  };

  return (
    <RegisterPane back={back} onSubmit={submit}>
      <PaneTitle>{t("auth.register.consentsTitle")}</PaneTitle>
      <p className="a-sub">{t("auth.register.consentsSub")}</p>
      {error ? <FormAlert title={error} onDismiss={onDismissError} /> : null}
      <div className="r-checks">
        <label className="a-chk">
          <input
            type="checkbox"
            className="checkbox"
            checked={terms}
            onChange={(event) => {
              onChange({ terms: event.target.checked });
            }}
          />
          <span>
            {t("auth.register.consentTermsLead")}{" "}
            <a href={LEGAL_LINKS.terms} target="_blank" rel="noopener noreferrer" className="a-link">
              {t("auth.common.termsOfService")}
            </a>{" "}
            {t("auth.register.consentTermsAnd")}{" "}
            <a href={LEGAL_LINKS.privacy} target="_blank" rel="noopener noreferrer" className="a-link">
              {t("auth.common.privacyNotice")}
            </a>
            . <span className="req">{t("auth.register.required")}</span>
          </span>
        </label>
        <label className="a-chk">
          <input
            type="checkbox"
            className="checkbox"
            checked={dpa}
            onChange={(event) => {
              onChange({ dpa: event.target.checked });
            }}
          />
          <span>
            {t("auth.register.consentDpaLead")} <b>{t("auth.register.consentDpaAct")}</b> {t("auth.register.consentDpaTail")}{" "}
            <span className="req">{t("auth.register.required")}</span>
          </span>
        </label>
        <label className="a-chk">
          <input
            type="checkbox"
            className="checkbox"
            checked={marketing}
            onChange={(event) => {
              onChange({ marketing: event.target.checked });
            }}
          />
          <span>{t("auth.register.consentMarketing")}</span>
        </label>
      </div>
      <SecurityCheck passed={token !== null} />
      <Turnstile action={TURNSTILE_ACTIONS.register} onToken={setToken} resetKey={captchaReset} />
      <RegisterNav backLabel={t("auth.register.back")} onBack={onBack}>
        <Button type="submit" size="lg" loading={submitting} disabled={!ready || submitting}>
          {t("auth.register.submit")}
        </Button>
      </RegisterNav>
    </RegisterPane>
  );
}
